"use client";

/**
 * Auth Context Provider (buyer)
 *
 * API 클라이언트를 DI 패턴으로 생성하여 Context로 제공
 * shared 레이어: config, workspace-pkg만 import 가능
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";

import { createApiClient, type ApiClient } from "@/shared/api/client";
import { createAuthService } from "@/shared/api/services/authService";
import { removeToken, setToken } from "@/shared/auth/cookie";
import type { AuthState, User } from "@/shared/auth/types";
import { API_BASE_URL } from "@/config/env";

export interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  loginWithKakao: (idKakao: string) => Promise<{ isNewUser: boolean }>;
  logout: () => void;
  deleteAccount: () => Promise<void>;
  apiClient: ApiClient;
}

/** @internal Storybook 데코레이터에서 mock 값 주입용으로 export */
export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
  initialUser?: User | null;
}

export function AuthProvider({ children, initialUser }: AuthProviderProps) {
  const router = useRouter();
  const queryClient = useQueryClient();

  const [user, setUser] = useState<User | null>(initialUser ?? null);
  const [isLoading, setIsLoading] = useState(initialUser == null);

  /**
   * 동기 토큰 게터 — ApiClientOptions은 () => string | null을 요구.
   * cookie.ts의 getToken은 async이지만 클라이언트에서 document.cookie를 직접 파싱하는
   * 동기 경로도 내부에 존재한다. Provider는 'use client'이므로 항상 브라우저 환경.
   * document.cookie를 직접 파싱하는 인라인 구현으로 동기성을 보장한다.
   */
  const getTokenSync = useCallback((): string | null => {
    const match = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith("token="));
    if (!match) return null;
    const raw = decodeURIComponent(match.slice("token=".length));
    return raw.startsWith("Bearer ") ? raw.slice(7) : raw;
  }, []);

  const handleUnauthorized = useCallback(
    (returnUrl: string) => {
      if (window.location.pathname === "/login") return;
      router.push("/login?returnUrl=" + encodeURIComponent(returnUrl));
    },
    [router],
  );

  const apiClient = useMemo(
    () =>
      createApiClient({
        baseUrl: API_BASE_URL,
        getToken: getTokenSync,
        onUnauthorized: handleUnauthorized,
      }),
    [getTokenSync, handleUnauthorized],
  );

  const authService = useMemo(() => createAuthService(apiClient), [apiClient]);

  // 마운트 시 사용자 정보 복원
  // 서버가 유효한 User를 제공하면 신뢰, 그 외에는 클라이언트 쿠키로 복구 시도
  // (결제 리다이렉트 후 Safari ITP 등으로 서버에서 쿠키를 못 읽는 경우 대응)
  useEffect(() => {
    if (initialUser) {
      // 서버가 유효한 사용자를 확인 — 신뢰
      setIsLoading(false);
      return;
    }

    // initialUser가 null/undefined — 클라이언트 쿠키 확인
    const token = getTokenSync();
    if (!token) {
      setIsLoading(false);
      return;
    }

    // 클라이언트에 토큰 존재 — 사용자 정보 복원 시도
    authService
      .getCurrentUser()
      .then((res) => {
        const { id, email, businessName, saleAmt, userGroups, point } = res.data;
        setUser({ id, email, businessName, saleAmt, userGroups, point: point ?? 0 });
      })
      .catch(() => {
        // 네트워크 오류/타임아웃 시 토큰 삭제하지 않음 — 토큰이 실제 만료되면
        // onUnauthorized(401)에서 로그인 페이지로 리다이렉트됨
        setUser(null);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [initialUser, authService, getTokenSync]);

  const login = useCallback(
    async (email: string, password: string) => {
      setIsLoading(true);
      try {
        const res = await authService.loginWithEmail({ email, memberPw: password });
        const { token, id, businessName, saleAmt, userGroups } = res.data;
        setToken("buyer", token);
        setUser({ id, email, businessName, saleAmt, userGroups, point: 0 });
        router.refresh();
        // 로그인 직후 포인트 등 전체 사용자 정보 로드
        authService
          .getCurrentUser()
          .then((userRes) => {
            const u = userRes.data;
            setUser({
              id: u.id,
              email: u.email,
              businessName: u.businessName,
              saleAmt: u.saleAmt,
              userGroups: u.userGroups,
              point: u.point ?? 0,
            });
          })
          .catch(() => {});
      } finally {
        setIsLoading(false);
      }
    },
    [authService, router],
  );

  const loginWithKakao = useCallback(
    async (idKakao: string): Promise<{ isNewUser: boolean }> => {
      setIsLoading(true);
      try {
        const res = await authService.loginWithKakao(idKakao);
        const result = res.data;

        if ("token" in result && result.token) {
          setToken("buyer", result.token);
          setUser({
            id: String(result.id),
            email: "",
            businessName: result.businessName,
            saleAmt: 0,
            userGroups: "",
            point: 0,
          });
          // 카카오 응답에는 userGroups/email/point가 없어 빈 값으로 시작한다.
          // 카트/체크아웃 가드(useCheckout.ts:142)는 user.userGroups가 비어있으면
          // 실제 DB 값(retailer 등)과 무관하게 "사업자 인증 필요" 다이얼로그를 띄운다.
          // 후속 페이지로 이동하기 전에 me로 정확한 값을 채운다.
          try {
            const userRes = await authService.getCurrentUser();
            const u = userRes.data;
            setUser({
              id: u.id,
              email: u.email,
              businessName: u.businessName,
              saleAmt: u.saleAmt,
              userGroups: u.userGroups,
              point: u.point ?? 0,
            });
          } catch {
            // 일시 네트워크 실패는 다음 마운트의 useEffect me 호출이 복구한다.
          }
          router.refresh();
          return { isNewUser: false };
        }

        localStorage.setItem("kakao_profile", JSON.stringify({ idKakao }));
        return { isNewUser: true };
      } finally {
        setIsLoading(false);
      }
    },
    [authService, router],
  );

  const logout = useCallback(() => {
    removeToken("buyer");
    queryClient.clear();
    setUser(null);
    router.push("/");
  }, [queryClient, router]);

  const deleteAccount = useCallback(async () => {
    await authService.deleteCurrentUser();
    removeToken("buyer");
    queryClient.clear();
    setUser(null);
  }, [authService, queryClient]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isAuthenticated: user !== null,
      isLoading,
      login,
      loginWithKakao,
      logout,
      deleteAccount,
      apiClient,
    }),
    [user, isLoading, login, loginWithKakao, logout, deleteAccount, apiClient],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** AuthContext 직접 접근용 (hooks.ts에서 사용) */
export function useAuthContext(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuthContext must be used within AuthProvider");
  }
  return ctx;
}
